/**
 * Authentication Service
 * 
 * Facade service that orchestrates authentication flows.
 * 
 * - Login/Logout/Refresh: Uses User API (which handles Cognito internally)
 * - Password Reset/Email Verification: Uses Cognito directly (until User API adds these endpoints)
 * 
 * Uses Platform_Core's createApiClient for authenticated API calls.
 * Pre-auth calls (login, refresh) use direct fetch since no token exists yet.
 * 
 * Requirements: 2.1, 2.2, 2.3, 2.4, 3.1, 3.2, 3.3, 7.1, 7.2, 7.3, 7.4
 */

import { createApiClient, type PlatformApiClient } from '@internal/platform-core';
import type { AuthResult, LoginResponse, TokenSet, User } from '../types/auth.types';
import { USER_API_URL } from '../config/api.config';
import { cognitoService } from './cognito.service';
import { tokenService } from './token.service';

/**
 * Supported social login providers
 */
export type SocialProvider = 'Google' | 'Amazon' | 'Apple';

/**
 * Login credentials
 */
export interface LoginCredentials {
  email: string;
  password: string;
  rememberMe?: boolean;
}

/**
 * Convert User API login response to our User type
 */
function mapLoginResponseToUser(response: LoginResponse): User {
  return {
    id: response.user.userId,
    cognitoId: response.user.userId,
    email: response.user.email,
    firstName: response.user.firstName,
    lastName: response.user.lastName,
    displayName: `${response.user.firstName} ${response.user.lastName}`.trim(),
    phoneNumber: undefined,
    userType: 'internal',
    status: 'active',
    roles: response.user.roles,
    groups: [],
    mfaEnabled: false,
    emailVerified: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
}

/**
 * Authentication Service Class
 */
export class AuthService {
  private api: PlatformApiClient;

  constructor() {
    // Create platform-core API client for authenticated User API calls
    this.api = createApiClient({
      baseUrl: USER_API_URL,
      authClient: {
        getAccessToken: () => tokenService.getAccessToken(),
        refreshToken: async () => false, // Auth-portal manages its own refresh
      },
    });
  }

  /**
   * Authenticate user with email and password via User API
   * Uses direct fetch — no token exists before login.
   */
  async login(credentials: LoginCredentials): Promise<AuthResult> {
    try {
      // Pre-auth call: direct fetch (no Bearer token available yet)
      const response = await fetch(`${USER_API_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: credentials.email, password: credentials.password }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const message = errorData?.error?.message
          || errorData?.message
          || (typeof errorData?.error === 'string' ? errorData.error : null)
          || `Login failed: ${response.status}`;
        throw new Error(message);
      }

      const loginResponse = await response.json() as LoginResponse;

      // Create token set
      const tokens: TokenSet = {
        accessToken: loginResponse.accessToken,
        refreshToken: loginResponse.refreshToken,
        idToken: '', // User API doesn't return ID token separately
        tokenType: 'Bearer',
        expiresIn: loginResponse.expiresIn,
      };

      // Store tokens
      tokenService.storeTokens(tokens);

      // Convert to our User type
      const user = mapLoginResponseToUser(loginResponse);

      // Check if profile selection is required
      if (loginResponse.requiresProfileSelection && loginResponse.profiles) {
        return {
          success: true,
          user,
          tokens,
          requiresProfileSelection: true,
          profiles: loginResponse.profiles,
        };
      }

      // Include active profile if auto-selected (single profile)
      return {
        success: true,
        user,
        tokens,
        activeProfile: loginResponse.activeProfile,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Authentication failed',
      };
    }
  }

  /**
   * Select a profile after login
   * Uses Platform_Core API client (token available after login).
   */
  async selectProfile(profileId: string): Promise<{ success: boolean; profile?: AuthResult['activeProfile']; error?: string }> {
    try {
      const accessToken = tokenService.getAccessToken();
      if (!accessToken) {
        return { success: false, error: 'No access token available' };
      }

      const response = await this.api.post<{ profile: { profileId: string; profileName: string; firstName?: string; lastName?: string; displayName?: string; roles: string[]; permissions: string[]; departmentId?: string; teamId?: string } }>('/auth/select-profile', { profileId });

      return {
        success: true,
        profile: {
          profileId: response.profile.profileId,
          profileName: response.profile.profileName,
          roles: response.profile.roles,
          permissions: response.profile.permissions,
        },
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Profile selection failed',
      };
    }
  }

  /**
   * Log out the current user via User API
   * Uses Platform_Core API client (token available during session).
   */
  async logout(): Promise<{ success: boolean; error?: string }> {
    try {
      const accessToken = tokenService.getAccessToken();

      if (accessToken) {
        await this.api.post('/auth/logout');
      }

      // Clear local token storage
      tokenService.clearTokens();

      return { success: true };
    } catch (error) {
      // Still clear tokens even if API call fails
      tokenService.clearTokens();
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Logout failed',
      };
    }
  }

  /**
   * Refresh tokens via User API
   * Uses direct fetch — refresh happens when the current token may be expired.
   */
  async refreshTokens(): Promise<AuthResult> {
    try {
      const refreshToken = tokenService.getRefreshToken();

      if (!refreshToken) {
        return {
          success: false,
          error: 'No refresh token available',
        };
      }

      // Pre-auth style call: direct fetch (current access token may be expired)
      const response = await fetch(`${USER_API_URL}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const message = errorData?.error?.message
          || errorData?.message
          || (typeof errorData?.error === 'string' ? errorData.error : null)
          || 'Token refresh failed';
        throw new Error(message);
      }

      const refreshResponse = await response.json() as LoginResponse;

      const tokens: TokenSet = {
        accessToken: refreshResponse.accessToken,
        refreshToken: refreshResponse.refreshToken,
        idToken: '',
        tokenType: 'Bearer',
        expiresIn: refreshResponse.expiresIn,
      };

      tokenService.storeTokens(tokens);

      const user = mapLoginResponseToUser(refreshResponse);

      return {
        success: true,
        user,
        tokens,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Token refresh failed',
      };
    }
  }

  /**
   * Initiate password reset flow (uses Cognito directly)
   * TODO: Move to User API when endpoint is available
   */
  async forgotPassword(email: string): Promise<{ success: boolean; error?: string }> {
    try {
      await cognitoService.initiatePasswordReset(email);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Password reset request failed',
      };
    }
  }

  /**
   * Reset password with verification code (uses Cognito directly)
   * TODO: Move to User API when endpoint is available
   */
  async resetPassword(
    email: string,
    code: string,
    newPassword: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      await cognitoService.confirmPasswordReset(email, code, newPassword);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Password reset failed',
      };
    }
  }

  /**
   * Change password for authenticated user (uses Cognito directly)
   * TODO: Move to User API when endpoint is available
   */
  async changePassword(
    oldPassword: string,
    newPassword: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const accessToken = tokenService.getAccessToken();
      await cognitoService.changePassword(oldPassword, newPassword, accessToken || undefined);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Password change failed',
      };
    }
  }

  /**
   * Register a new customer account (uses Cognito signUp directly)
   * Creates a Cognito user with a customer role.
   * After registration, the user must verify their email.
   *
   * Requirements: 3.4
   */
  async register(data: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
  }): Promise<{ success: boolean; error?: string }> {
    try {
      await cognitoService.createUser({
        email: data.email,
        password: data.password,
        firstName: data.firstName,
        lastName: data.lastName,
      });
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Registration failed',
      };
    }
  }

  /**
   * Verify email with confirmation code (uses Cognito directly)
   * TODO: Move to User API when endpoint is available
   */
  async verifyEmail(email: string, code: string): Promise<{ success: boolean; error?: string }> {
    try {
      await cognitoService.confirmSignUp(email, code);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Email verification failed',
      };
    }
  }

  /**
   * Resend verification email (uses Cognito directly)
   * TODO: Move to User API when endpoint is available
   */
  async resendVerification(email: string): Promise<{ success: boolean; error?: string }> {
    try {
      await cognitoService.resendVerificationCode(email);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to resend verification',
      };
    }
  }

  /**
   * Get current authenticated user
   * Uses Platform_Core API client for token validation.
   */
  async getCurrentUser(): Promise<User | null> {
    try {
      const accessToken = tokenService.getAccessToken();

      if (!accessToken || tokenService.isTokenExpired(accessToken)) {
        return null;
      }

      // Validate token with User API using platform-core client
      const validation = await this.api.post<{ valid: boolean; user?: { userId: string; email: string; firstName: string; lastName: string; phoneNumber?: string; userType?: string; status?: string; roles?: string[]; groups?: string[]; createdAt?: string; updatedAt?: string } }>('/auth/validate', { token: accessToken });

      if (!validation.valid || !validation.user) {
        return null;
      }

      const profile = validation.user;

      return {
        id: profile.userId,
        cognitoId: profile.userId,
        email: profile.email,
        firstName: profile.firstName,
        lastName: profile.lastName,
        displayName: `${profile.firstName} ${profile.lastName}`.trim(),
        phoneNumber: profile.phoneNumber,
        userType: (profile.userType as 'internal' | 'external_partner' | 'customer') || 'internal',
        status: (profile.status as 'active' | 'suspended' | 'inactive') || 'active',
        roles: profile.roles || [],
        groups: profile.groups || [],
        mfaEnabled: false,
        emailVerified: true,
        createdAt: profile.createdAt || new Date().toISOString(),
        updatedAt: profile.updatedAt || new Date().toISOString(),
      };
    } catch (error) {
      console.error('Failed to get current user:', error);
      return null;
    }
  }

  /**
   * Check if user is authenticated
   */
  async isAuthenticated(): Promise<boolean> {
    const user = await this.getCurrentUser();
    return user !== null;
  }
}

// Export singleton instance
export const authService = new AuthService();
